import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import ButtonIcon from 'components/ButtonIcon';
import Pencil from 'components/icons/pencil2';
import useQueryFilters from 'pages/MemoryManagement/hooks/useQueryFilters';
import useManageCustomerMemory from 'data/hooks/useManageCustomerMemory';
import { EDIT_MEMORY_MODAL } from '../../../../constants';
import { Memory as MemoryType, TYPE_COLORS, TYPE_LABELS } from '../../types';
import {
    MemoryCard,
    MemoryHeader,
    TypeBadge,
    ImportanceBadge,
    MemoryContent,
    MemoryFooter,
    FooterItem,
    SegmentTag,
    TimeStamp,
    SourceBadge,
    FooterItems,
} from './styled';

interface MemoryProps {
    value: MemoryType;
}

const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString();
};

const Memory = ({ value }: MemoryProps) => {
    const {
        type,
        content,
        importance,
        source,
        segment,
        timeMark,
        inputs,
    } = value;
    const queryClient = useQueryClient();

    const { agentId = '' } = useParams();
    const filters = useQueryFilters();
    const { edit } = useManageCustomerMemory({
        modalId: EDIT_MEMORY_MODAL,
        agentId,
        customerId: filters.customerId,
        onMemoryUpdated: () => {
            queryClient.invalidateQueries({
                queryKey: ['memories', agentId, ...Object.values(filters)],
            });
        },
    });

    const typeColor = TYPE_COLORS[type as keyof typeof TYPE_COLORS];
    const typeLabel = TYPE_LABELS[type as keyof typeof TYPE_LABELS];

    return (
        <MemoryCard>
            <MemoryHeader>
                <div>
                    <TypeBadge color={typeColor}>{typeLabel}</TypeBadge>
                    <ImportanceBadge>
                        Importance:
                        {' '}
                        {importance}
                    </ImportanceBadge>
                </div>
                <TimeStamp>{formatTimestamp(timeMark)}</TimeStamp>
            </MemoryHeader>

            <MemoryContent>{content}</MemoryContent>

            <MemoryFooter>
                <FooterItems>
                    {segment && (
                        <FooterItem>
                            <SegmentTag>{segment}</SegmentTag>
                        </FooterItem>
                    )}

                    {source && (
                        <FooterItem>
                            <SourceBadge isExternal={source.type === 'external'}>
                                {source.type}
                            </SourceBadge>
                        </FooterItem>
                    )}

                    {inputs && inputs.length > 0 && (
                        <FooterItem>
                            Inputs:
                            {' '}
                            {inputs.join(', ')}
                        </FooterItem>
                    )}
                </FooterItems>
                <ButtonIcon icon={<Pencil />} borderRadius="semi-square" size="small" onClick={() => edit(value)} />
            </MemoryFooter>
        </MemoryCard>
    );
};

export default Memory;
